import React from 'react';
import { useProductDetailQuery, useProductListQuery } from '@/api/queries';
import type { ProductVariant } from '@/api/models';
import type { ExistingLinePick } from '../useManualImport';

/**
 * Drives the "Add product" picker: a two-stage flow (search the catalog, then
 * pick a variant) plus open/close state. Rendered inside a HeroUI `Popover`, so
 * dismissal (outside click / Escape) and portalling are handled by the overlay
 * — this hook only owns the data and the stage transitions.
 */
export const useProductPicker = (onPick: (pick: ExistingLinePick) => void) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [productId, setProductId] = React.useState<string | null>(null);

    const listQuery = useProductListQuery(
        { page: 1, pageSize: 20, search: search.trim() || undefined },
        isOpen,
    );
    const detailQuery = useProductDetailQuery(productId);

    const products = listQuery.data?.success
        ? listQuery.data.response.items
        : [];
    const detail = detailQuery.data?.success ? detailQuery.data.response : null;
    const variants = detail?.variants ?? [];

    // Each open starts fresh on the search stage.
    const onOpenChange = React.useCallback((open: boolean) => {
        setIsOpen(open);
        if (!open) {
            setSearch('');
            setProductId(null);
        }
    }, []);

    const onSelectProduct = React.useCallback(
        (id: string) => setProductId(id),
        [],
    );
    const onBackToSearch = React.useCallback(() => setProductId(null), []);

    const onPickVariant = React.useCallback(
        (variant: ProductVariant) => {
            if (!detail) {
                return;
            }
            // Flatten the variant's chosen option values into the draft's
            // attributeTypeId → attributeValueId map.
            const optionValues: Record<string, string> = {};
            for (const option of variant.options) {
                optionValues[option.attributeTypeId] = option.attributeValueId;
            }
            onPick({
                variantId: variant.id,
                productId: detail.id,
                name: detail.name,
                sku: detail.sku,
                barcode: variant.barcode,
                categoryId: detail.category?.id ?? null,
                brandId: detail.brand?.id ?? null,
                unit: detail.unit,
                vatType: detail.vatType,
                optionValues,
                salePrice: variant.salePrice,
                wholesalePrice: variant.wholesalePrice,
                minimumQuantity: variant.minimumQuantity,
            });
            onOpenChange(false);
        },
        [detail, onOpenChange, onPick],
    );

    return {
        isOpen,
        onOpenChange,
        search,
        onSearchChange: setSearch,
        productId,
        onSelectProduct,
        onBackToSearch,
        products,
        variants,
        onPickVariant,
    };
};
